import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { Badge } from "@shared/ui/badge";
import { Eye } from "lucide-react";
import { useGetWorkflowsExecutionAllChannelRecipients } from "@features/ux/components/workflow/hooks";

const EmailList = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read search query and page from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Replace this with your actual API hook
  const { data: emailsData } = useGetWorkflowsExecutionAllChannelRecipients(
    id,
    "email",
    currentPage,
    MODAL_PAGE_SIZE,
    searchQuery
  );

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: MODAL_PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = emailsData?.count || 0;
    const totalPages = Math.ceil(totalItems / MODAL_PAGE_SIZE);
    const startItem = (currentPage - 1) * MODAL_PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * MODAL_PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: MODAL_PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [emailsData?.count, currentPage]);

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Define table columns
  const columns = useMemo<TableColumn<any>[]>(
    () => [
      {
        key: "sendTime",
        header: "Send Time",
        sortable: true,
      },
      {
        key: "accountNumber",
        header: "Account Number",
        sortable: true,
      },
      {
        key: "name",
        header: "Name",
        sortable: true,
      },
      {
        key: "email",
        header: "Email",
        sortable: true,
      },
      {
        key: "department",
        header: "Department",
        sortable: true,
      },
      {
        key: "role",
        header: "Role",
        sortable: true,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        render: (value) => (
          <Badge
            variant={value === "Delivered" ? "default" : "secondary"}
            className={
              value === "Delivered"
                ? "bg-green-500 hover:bg-green-600"
                : "bg-red-500 hover:bg-red-600"
            }
          >
            {value}
          </Badge>
        ),
      },
      {
        key: "engagement",
        header: "Engagement",
        sortable: false,
        render: (value: string[]) => (
          <div className="flex gap-2">
            {value && value?.length > 0 ? (
              value.map((engagement, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className={
                    engagement === "Opened"
                      ? "border-blue-500 text-blue-700"
                      : "border-purple-500 text-purple-700"
                  }
                >
                  <Eye className="w-3 h-3 mr-1" />
                  {engagement}
                </Badge>
              ))
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <GenericTable
        data={emailsData?.results || []}
        columns={columns}
        rowKey="id"
        emptyMessage="No emails found"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search emails..."
      />
    </div>
  );
};

export default EmailList;
